import type { UpdatePayload } from 'vite';
import type { Component, MaybeRefOrGetter, Ref } from 'vue';
import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import { markRaw, onBeforeUnmount, shallowRef, toValue, watch } from 'vue';
import { loadHrmUpdateModule } from '#layers/base/public';
import { isBlockDefinition } from '#layers/block-engine/utils/block/editor';

export function useReactiveBlockComponent<D extends BlockDefinition | BlockSerialized | undefined>(block: MaybeRefOrGetter<D>): D extends BlockDefinition ? Ref<Component> : Ref<null> {
	const comp = shallowRef<any>(null);

	watch(() => toValue(block), async (_block) => {
		if (isBlockDefinition(_block)) {
			comp.value = _block.comp;
		}
		else {
			comp.value = null;
		}
	}, {
		immediate: true,
	});

	if (import.meta.hot) {
		const onUpdate = async (payload: UpdatePayload) => {
			if (comp.value) {
				const file = comp.value.__file;
				const update = payload.updates.find((u) => u.path.includes(file));

				if (update) {
					const mod = await loadHrmUpdateModule(update);
					comp.value = markRaw(mod.default ?? mod);
				}
			}
		};

		import.meta.hot.on('vite:afterUpdate', onUpdate);

		onBeforeUnmount(() => {
			// Component-test HMR clients may omit listener cleanup; Vite still unsubscribes normally.
			import.meta.hot?.off?.('vite:afterUpdate', onUpdate);
		});
	}

	return comp;
}
